import { useEffect, useRef, type RefObject } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Boxes,
  LayoutDashboard, FileText, Receipt, Users, HardHat,
  Wallet, Bell, Settings as SettingsIcon, X, CalendarCheck, Plus,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { isFieldRole } from "@dker/core";
import { TenantMark } from "@/components/shared/TenantMark";
import { useTenantOptional } from "@/hooks/useTenant";
import { useBranding } from "@/hooks/useTenantConfig";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { activeChild, activeGroup, NAV_GROUPS } from "@/lib/navigation";
import { cn } from "@/lib/utils";

interface NavEntry {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
}

/**
 * Les icônes des huit groupes. La structure, elle, vit dans
 * `lib/navigation.ts` : la palette de création la lit aussi, et deux copies
 * de la carte finiraient par ne plus mener aux mêmes endroits.
 *
 * Chacune de ces entrées mène à un écran qu'un compte ouvrier n'a pas le droit
 * de lire — la base le refuse depuis la migration 20260301001100. Les lui
 * montrer quand même donnerait une application qui a l'air cassée.
 */
const ICONES: Record<string, LucideIcon> = {
  pilotage: LayoutDashboard,
  vendre: FileText,
  encaisser: Receipt,
  produire: HardHat,
  equipe: Users,
  acheter: Wallet,
  materiel: Boxes,
  alertes: Bell,
};

/**
 * Le menu du terrain.
 *
 * Volontairement réduit à une entrée. Un ouvrier ouvre l'application le matin
 * pour savoir où il va ; tout le reste est du bruit sur un téléphone tenu
 * d'une main.
 */
const NAV_TERRAIN: NavEntry[] = [
  { to: "/ma-journee", icon: CalendarCheck, label: "Ma journée", end: true },
];

/** Où l'élan emporterait le menu si on le laissait filer (voir Dialog). */
function projeter(vitesse: number, deceleration = 0.998): number {
  return ((vitesse / 1000) * deceleration) / (1 - deceleration);
}

/**
 * Balayer le menu vers la gauche pour le refermer, au doigt.
 *
 * Le menu suit le doigt une fois le geste reconnu comme horizontal (10 px de
 * marge, pour ne pas voler un défilement vertical de la liste). Au lâcher,
 * c'est l'élan projeté qui décide, pas seulement la position ; un lien sous
 * le doigt n'est pas suivi. Vers la droite, le menu résiste au lieu de se
 * bloquer net.
 */
function useBalayerPourFermer(
  ref: RefObject<HTMLElement | null>,
  actif: boolean,
  fermer: () => void,
) {
  const fermerRef = useRef(fermer);
  fermerRef.current = fermer;

  useEffect(() => {
    const el = ref.current;
    if (!el || !actif) return;
    let depart: { x: number; y: number; id: number } | null = null;
    let glisse = false;
    let releves: { x: number; t: number }[] = [];

    const relacher = () => {
      el.style.transition = "";
      el.style.translate = "";
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      depart = { x: e.clientX, y: e.clientY, id: e.pointerId };
      glisse = false;
      releves = [];
    };
    const move = (e: PointerEvent) => {
      if (!depart || e.pointerId !== depart.id) return;
      const dx = e.clientX - depart.x;
      const dy = e.clientY - depart.y;
      if (!glisse) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          depart = null; // un défilement : on le laisse
          return;
        }
        if (Math.abs(dx) <= 10 || Math.abs(dx) <= Math.abs(dy)) return;
        glisse = true;
        el.setPointerCapture(e.pointerId);
        el.style.transition = "none";
      }
      const largeur = el.offsetWidth;
      const x = dx <= 0 ? dx : (dx * largeur * 0.55) / (largeur + 0.55 * dx);
      el.style.translate = `${x}px 0`;
      releves.push({ x, t: e.timeStamp });
      releves = releves.filter((r) => e.timeStamp - r.t <= 100);
    };
    const up = (e: PointerEvent) => {
      if (!depart || e.pointerId !== depart.id) return;
      depart = null;
      if (!glisse) return;
      glisse = false;
      // La vitesse au lâcher : un doigt arrêté avant de se lever n'a pas d'élan.
      const x = releves[releves.length - 1]?.x ?? 0;
      releves = releves.filter((r) => e.timeStamp - r.t <= 100);
      const a = releves[0];
      const b = releves[releves.length - 1];
      const vitesse = a && b && b.t > a.t ? ((b.x - a.x) / (b.t - a.t)) * 1000 : 0;
      // Le clic qui suit un balayage n'est pas une intention d'ouvrir un lien.
      const avaler = (c: Event) => {
        c.preventDefault();
        c.stopPropagation();
      };
      el.addEventListener("click", avaler, { capture: true, once: true });
      window.setTimeout(() => el.removeEventListener("click", avaler, { capture: true }), 300);
      // La transition CSS repart de la position affichée, dans un sens ou
      // dans l'autre.
      relacher();
      if (x + projeter(vitesse) < -el.offsetWidth / 2) fermerRef.current();
    };
    const cancel = () => {
      depart = null;
      glisse = false;
      relacher();
    };

    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", cancel);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", cancel);
      relacher();
    };
  }, [ref, actif]);
}

export function Sidebar() {
  const tenant = useTenantOptional();
  const branding = useBranding();
  const unread = useAuthStore((s) => s.unreadAlerts);
  // Le rôle décide du menu, pas une préférence : ce qui est masqué ici est de
  // toute façon refusé par la base.
  const estTerrain = isFieldRole(useAuthStore((s) => s.user?.role));
  const utilisateur = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const section = activeGroup(pathname);
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const mobileOpen = useUiStore((s) => s.sidebarMobileOpen);
  const setMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);
  const menu = useRef<HTMLElement>(null);
  useBalayerPourFermer(menu, mobileOpen, () => setMobileOpen(false));

  // Échap referme le menu du téléphone, comme une fenêtre.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, setMobileOpen]);

  return (
    <>
      {/* Le voile du téléphone. Toujours présent, pour s'estomper avec le
          menu qui glisse au lieu d'apparaître d'un coup à côté de lui. */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/60 lg:hidden transition-opacity duration-300 motion-reduce:transition-none",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={() => setMobileOpen(false)}
        aria-hidden
      />
      <aside
        ref={menu}
        className={cn(
          // Même courbe à l'aller et au retour : le menu repart par où il est venu.
          "fixed inset-y-0 left-0 z-50 w-[260px] bg-bg-secondary border-r border-border flex flex-col touch-pan-y",
          "transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex items-center justify-between px-4 h-14 lg:h-16 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <TenantMark
              name={tenant?.name}
              logoUrl={branding.logoUrl}
              className="w-9 h-9"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-text-primary truncate">
                {tenant?.name ?? "DKER"}
              </p>
              {/* Le nom de la personne, et non le plan d'abonnement, qui ne
                  change jamais et se lit dans les réglages. Sur un téléphone
                  partagé entre deux salariés, savoir qui est connecté est la
                  seule information qui compte ici. */}
              {utilisateur?.full_name ? (
                <p className="text-[10px] text-text-muted truncate">
                  {utilisateur.full_name}
                </p>
              ) : tenant ? (
                <p className="text-[10px] text-text-muted truncate">
                  Plan {tenant.plan}
                </p>
              ) : null}
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-2 text-text-muted hover:text-text-primary"
            aria-label="Fermer le menu"
          >
            <X size={18} />
          </button>
        </div>

        {!estTerrain && (
          // La porte visible de la palette. Le raccourci seul serait un geste
          // que personne ne découvre : ce qui ne se voit pas ne s'utilise pas.
          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                setPaletteOpen(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 bg-accent hover:bg-accent-hover text-accent-contrast text-sm font-semibold touch-target"
            >
              <Plus size={18} />
              <span className="flex-1 text-left">Créer</span>
              <kbd className="hidden lg:inline font-mono text-[10.5px] opacity-80">Ctrl K</kbd>
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5 touch-pan-y">
          {estTerrain
            ? NAV_TERRAIN.map(({ to, icon: Icon, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => lien(isActive)}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  <span className="flex-1">{label}</span>
                </NavLink>
              ))
            : NAV_GROUPS.map((groupe) => {
                const Icon = ICONES[groupe.id] ?? LayoutDashboard;
                const ouvert = section?.id === groupe.id;
                const enfant = ouvert ? activeChild(pathname, groupe) : null;
                return (
                  <div key={groupe.id}>
                    {/* Le libellé du groupe mène à son enfant le plus fréquent :
                        « Acheter » ouvre Dépenses. Un groupe à déplier aurait
                        ajouté un clic à toutes les destinations — le remède pire
                        que le mal que le plan annonçait. */}
                    <NavLink
                      to={groupe.to}
                      end={groupe.to === "/"}
                      onClick={() => setMobileOpen(false)}
                      className={() => lien(ouvert)}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      <span className="flex-1">{groupe.label}</span>
                      {groupe.id === "alertes" && unread > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-danger text-white">
                          {unread}
                        </span>
                      )}
                    </NavLink>

                    {/* Les frères, dépliés dans la section courante seulement :
                        le cas rare cesse d'être invisible, sans allonger le
                        menu quand on est ailleurs. */}
                    {ouvert && groupe.children.length > 1 && (
                      <div className="ml-[42px] mt-0.5 mb-1.5 border-l border-border">
                        {groupe.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              "block pl-3 pr-2 py-1.5 text-[13px] -ml-px border-l-2 transition-colors",
                              enfant?.to === c.to
                                ? "border-accent text-text-primary font-medium"
                                : "border-transparent text-text-muted hover:text-text-primary",
                            )}
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
        </nav>

        {!estTerrain && (
        <div className="p-3 border-t border-border">
          <NavLink
            to="/parametres"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => lien(isActive)}
          >
            <SettingsIcon size={18} />
            Paramètres
          </NavLink>
        </div>
        )}
      </aside>
    </>
  );
}

function lien(actif: boolean): string {
  return cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors touch-target",
    actif
      ? "bg-accent/15 text-accent-ink"
      : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary",
  );
}
